import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Briefcase, Plus, List, LayoutGrid } from 'lucide-react';
import { toast } from 'sonner';
import { jobsAPI, JOB_TYPE_LABELS, JOB_STATUS_LABELS, JOB_STATUS_ORDER, JOB_STATUS_COLORS, JOB_PRIORITY_COLORS } from '../lib/jobsAPI';
import { clientsAPI } from '../lib/clientsAPI';
import { authAPI } from '../lib/api';
import { formatAUDate } from '../lib/dateUtils';

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

export default function Jobs() {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list' or 'board'
  const [filters, setFilters] = useState({ status: 'all', job_type: 'all', client_id: 'all', priority: 'all' });
  const [clients, setClients] = useState([]);
  const [role, setRole] = useState(null);

  const isClient = role === 'client';

  useEffect(() => {
    loadRole();
  }, []);

  const loadRole = async () => {
    try {
      const response = await authAPI.getMe();
      const r = response.data.active_membership?.role;
      setRole(r);
      // Staff can list all clients for the filter; clients cannot and don't need to.
      if (r !== 'client') loadClients();
    } catch (error) {
      // Fall back to staff behaviour if role can't be determined.
      loadClients();
    }
  };

  const loadJobs = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v !== 'all') params[k] = v;
      });
      const response = await jobsAPI.list(params);
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const loadClients = async () => {
    try {
      const response = await clientsAPI.list({ status: 'active' });
      setClients(response.data);
    } catch (error) {
      console.error('Failed to load clients');
    }
  };

  // Re-fetch whenever a filter changes.
  useEffect(() => {
    loadJobs();
  }, [filters]);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  // Board columns: collapse the long lifecycle into 5 readable buckets.
  const boardColumns = [
    { id: 'todo', title: 'To Do', statuses: ['not_started', 'waiting_for_client'] },
    { id: 'documents', title: 'Docs / In Progress', statuses: ['documents_received', 'in_progress'] },
    { id: 'review', title: 'Review', statuses: ['ai_review', 'accountant_review', 'partner_review'] },
    { id: 'approval', title: 'Approval / Lodge', statuses: ['ready_for_client_approval', 'ready_to_lodge'] },
    { id: 'done', title: 'Completed', statuses: ['completed', 'archived'] },
  ];

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }} data-testid="jobs-title">
            {isClient ? 'My Jobs' : 'Jobs'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {isClient ? 'Track the status of your accounting work' : 'All accounting work across the practice'}
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex border rounded-lg p-1">
            <Button
              variant={view === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setView('list')}
              className={view === 'list' ? 'bg-primary hover:bg-primary/90' : ''}
            >
              <List className="w-4 h-4 mr-2" />
              List
            </Button>
            <Button
              variant={view === 'board' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setView('board')}
              className={view === 'board' ? 'bg-primary hover:bg-primary/90' : ''}
            >
              <LayoutGrid className="w-4 h-4 mr-2" />
              Board
            </Button>
          </div>
          {!isClient && (
            <Button className="bg-primary hover:bg-primary/90" onClick={() => navigate('/jobs/new')} data-testid="add-job-button">
              <Plus className="w-4 h-4 mr-2" />
              New Job
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className={`grid grid-cols-2 gap-3 ${isClient ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
            <Select value={filters.status} onValueChange={(v) => setFilter('status', v)}>
              <SelectTrigger data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {JOB_STATUS_ORDER.map((s) => (
                  <SelectItem key={s} value={s}>{JOB_STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.job_type} onValueChange={(v) => setFilter('job_type', v)}>
              <SelectTrigger data-testid="filter-type"><SelectValue placeholder="Job Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(JOB_TYPE_LABELS).map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!isClient && (
              <Select value={filters.client_id} onValueChange={(v) => setFilter('client_id', v)}>
                <SelectTrigger data-testid="filter-client"><SelectValue placeholder="Client" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={filters.priority} onValueChange={(v) => setFilter('priority', v)}>
              <SelectTrigger data-testid="filter-priority"><SelectValue placeholder="Priority" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {jobs.length === 0 ? (
        <Card>
          <CardContent className="text-center py-16 text-muted-foreground">
            <Briefcase className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
            <p>{isClient ? 'You have no jobs yet' : 'No jobs match these filters'}</p>
            {!isClient && (
              <Button className="mt-4 bg-primary hover:bg-primary/90" onClick={() => navigate('/jobs/new')}>
                <Plus className="w-4 h-4 mr-2" />
                Create the first job
              </Button>
            )}
          </CardContent>
        </Card>
      ) : view === 'board' ? (
        /* Board view */
        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">
          {boardColumns.map((col) => {
            const colJobs = jobs.filter((j) => col.statuses.includes(j.status));
            return (
              <Card key={col.id} className="bg-muted/50" data-testid={`board-column-${col.id}`}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center justify-between" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                    <span>{col.title}</span>
                    <Badge variant="outline">{colJobs.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {colJobs.length === 0 ? (
                    <p className="text-xs text-muted-foreground/70 text-center py-6">—</p>
                  ) : (
                    colJobs.map((job) => (
                      <Card
                        key={job.id}
                        className="bg-card hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => navigate(`/jobs/${job.id}`)}
                        data-testid={`job-card-${job.id}`}
                      >
                        <CardContent className="p-3 space-y-2">
                          <h4 className="font-medium text-sm text-foreground line-clamp-2">{job.title}</h4>
                          <p className="text-xs text-muted-foreground">{JOB_TYPE_LABELS[job.job_type]}</p>
                          {job.client_name && <p className="text-xs text-muted-foreground">{job.client_name}</p>}
                          <div className="flex items-center justify-between pt-1">
                            <Badge className={`${JOB_STATUS_COLORS[job.status]} text-[10px]`}>
                              {JOB_STATUS_LABELS[job.status]}
                            </Badge>
                            {job.assignee_name && (
                              <Avatar className="w-6 h-6">
                                <AvatarFallback className="bg-primary text-white text-[10px]">
                                  {getInitials(job.assignee_name)}
                                </AvatarFallback>
                              </Avatar>
                            )}
                          </div>
                          {job.due_at && <p className="text-xs text-muted-foreground">Due: {formatAUDate(job.due_at)}</p>}
                        </CardContent>
                      </Card>
                    ))
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* List view */
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50 text-left text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Job</th>
                    <th className="px-4 py-3 font-medium">Type</th>
                    <th className="px-4 py-3 font-medium">Client</th>
                    <th className="px-4 py-3 font-medium">Period</th>
                    <th className="px-4 py-3 font-medium">Due</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Priority</th>
                    <th className="px-4 py-3 font-medium">Assignee</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr
                      key={job.id}
                      className="border-b hover:bg-muted/50 cursor-pointer"
                      onClick={() => navigate(`/jobs/${job.id}`)}
                      data-testid={`job-row-${job.id}`}
                    >
                      <td className="px-4 py-3 font-medium text-foreground">{job.title}</td>
                      <td className="px-4 py-3 text-muted-foreground">{JOB_TYPE_LABELS[job.job_type]}</td>
                      <td className="px-4 py-3 text-muted-foreground">{job.client_name || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{job.period_label || '—'}</td>
                      <td className="px-4 py-3 text-muted-foreground">{job.due_at ? formatAUDate(job.due_at) : '—'}</td>
                      <td className="px-4 py-3">
                        <Badge className={`${JOB_STATUS_COLORS[job.status]} text-xs`}>
                          {JOB_STATUS_LABELS[job.status]}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={`${JOB_PRIORITY_COLORS[job.priority]} text-xs`}>
                          {job.priority.charAt(0).toUpperCase() + job.priority.slice(1)}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        {job.assignee_name ? (
                          <div className="flex items-center gap-2">
                            <Avatar className="w-7 h-7">
                              <AvatarFallback className="bg-primary text-white text-xs">
                                {getInitials(job.assignee_name)}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-muted-foreground">{job.assignee_name}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground/70">Unassigned</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
